import { useState, useCallback, useRef, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";

/**
 * Configuration for lazy loading behavior
 */
export interface LazyLoadingConfig<T> {
  /** API endpoint for fetching data (without query params) */
  endpoint: string;

  /** Number of items to fetch per page */
  pageSize?: number;

  /** Default sort field */
  sortField?: string;

  /** Default sort direction */
  sortDirection?: "ASC" | "DESC";

  /** Transform response data before setting state */
  transformResponse?: (data: any) => T[];

  /** Additional query parameters to include in every request */
  additionalParams?: Record<string, string | number | boolean>;

  /** Callback when fetch succeeds */
  onFetchSuccess?: (data: T[], append: boolean) => void;

  /** Callback when fetch fails */
  onFetchError?: (error: any) => void;

  autoFetch?: boolean;

  /** Pre-loaded data to display immediately (skips initial fetch) */
  initialData?: T[];

  /** Starting page number when using initialData (so pagination continues correctly) */
  initialPage?: number;

  /** Whether more pages exist when using initialData */
  initialHasMore?: boolean;
}

/**
 * State returned by useLazyLoading hook
 */
export interface LazyLoadingState<T> {
  /** Current data array */
  data: T[];

  /** Current page number (1-indexed) */
  currentPage: number;

  /** Whether more data exists to load */
  hasMore: boolean;

  /** Total count of all items (from server) */
  totalCount: number | null;

  /** Loading state for initial fetch */
  isLoading: boolean;

  /** Loading state for appending more data */
  isLoadingMore: boolean;

  /** Fetch function - call with append=false to reset, append=true to load more */
  fetch: (append?: boolean) => Promise<void>;

  /** Ref for IntersectionObserver target */
  observerTarget: React.RefObject<HTMLDivElement>;

  /** Manually set data (useful for optimistic updates) */
  setData: React.Dispatch<React.SetStateAction<T[]>>;

  /** Reset to initial state */
  reset: () => void;
}

/**
 * Reusable hook for implementing lazy loading with infinite scroll
 *
 * @example
 * const { data, isLoading, isLoadingMore, hasMore, observerTarget } = useLazyLoading({
 *   endpoint: '/api/contacts',
 *   pageSize: 50,
 *   sortField: 'id',
 *   sortDirection: 'DESC',
 *   additionalParams: { searchText, tags: tags.join(',') }
 * });
 *
 * @template T - Type of data items
 */
export function useLazyLoading<T = any>(
  config: LazyLoadingConfig<T>,
): LazyLoadingState<T> {
  const {
    endpoint,
    pageSize = 50,
    sortField = "id",
    sortDirection = "DESC",
    transformResponse,
    additionalParams = {},
    onFetchSuccess,
    onFetchError,
    initialData,
    initialPage,
    initialHasMore,
  } = config;

  const hasInitialData = initialData && initialData.length > 0;

  const { session } = useSession();

  // State - use initial values from cache when available
  const [data, setData] = useState<T[]>(hasInitialData ? initialData : []);
  const [currentPage, setCurrentPage] = useState(hasInitialData && initialPage ? initialPage : 1);
  const [hasMore, setHasMore] = useState(hasInitialData && initialHasMore !== undefined ? initialHasMore : true);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Refs
  const observerTarget = useRef<HTMLDivElement>(null);
  const initialMountRef = useRef(true);
  const lastFetchTime = useRef<number>(0);
  const isLoadingMoreRef = useRef(false); // Track loading state without closure issues
  const currentPageRef = useRef(hasInitialData && initialPage ? initialPage : 1); // Track current page without causing fetch recreation
  const additionalParamsRef = useRef(additionalParams); // Store params to avoid fetch recreation
  const previousParamsRef = useRef<string>(JSON.stringify(additionalParams)); // Track previous params for change detection
  const pageSizeRef = useRef(pageSize); // Store pageSize to handle dynamic changes
  const previousPageSizeRef = useRef(pageSize); // Track previous pageSize for change detection
  const previousEndpointRef = useRef(endpoint); // Track previous endpoint for change detection
  const sortFieldRef = useRef(sortField); // Store sortField
  const sortDirectionRef = useRef(sortDirection); // Store sortDirection
  const transformResponseRef = useRef(transformResponse);
  const onFetchSuccessRef = useRef(onFetchSuccess);
  const onFetchErrorRef = useRef(onFetchError);
  const sessionRef = useRef(session); // Store session to make fetch completely stable
  const hasMoreRef = useRef(hasInitialData && initialHasMore !== undefined ? initialHasMore : true); // Store hasMore to avoid observer recreation
  const isLoadingRef = useRef(false); // Store isLoading to avoid stale closures
  const dataLengthRef = useRef(hasInitialData ? initialData.length : 0); // Store data length to avoid stale closures
  const observerInstanceRef = useRef<IntersectionObserver | null>(null); // Store observer to allow re-attachment

  // Update refs when values change (keeps fetch and observer stable while allowing latest behavior)
  useEffect(() => {
    additionalParamsRef.current = additionalParams;
    pageSizeRef.current = pageSize; // Update pageSize ref when it changes
    // NOTE: previousEndpointRef is NOT updated here - it's only updated in the endpoint change detection effect
    // to ensure we can detect when endpoint actually changes
    sortFieldRef.current = sortField;
    sortDirectionRef.current = sortDirection;
    transformResponseRef.current = transformResponse;
    onFetchSuccessRef.current = onFetchSuccess;
    onFetchErrorRef.current = onFetchError;
    sessionRef.current = session;
    hasMoreRef.current = hasMore;
    isLoadingRef.current = isLoading;
    dataLengthRef.current = data.length;

    console.log('📝 [useLazyLoading] Refs updated - pageSize:', pageSize, 'sortField:', sortField, 'endpoint:', endpoint);
  }, [
    additionalParams,
    pageSize, // Add pageSize to dependencies
    // NOTE: endpoint is intentionally NOT in dependencies to avoid updating previousEndpointRef prematurely
    sortField,
    sortDirection,
    transformResponse,
    onFetchSuccess,
    onFetchError,
    session,
    hasMore,
    isLoading,
    data.length,
  ]);

  // Debounce cooldown for lazy loading (prevent multiple fetches)
  const FETCH_COOLDOWN = 2000; // 2 seconds

  /**
   * Fetch data from API
   * @param append - If true, appends to existing data. If false, replaces data.
   */
  const fetch = useCallback(
    async (append = false) => {
      if (!sessionRef.current) return;

      // Set appropriate loading state
      if (append) {
        setIsLoadingMore(true);
        // Note: isLoadingMoreRef is now set in observer callback BEFORE fetch is called
      } else {
        setIsLoading(true);
        setData([]);
        setCurrentPage(1);
        currentPageRef.current = 1; // Sync ref
        setHasMore(true);
        lastFetchTime.current = 0; // Reset debounce timer on filter changes
      }

      // Determine which page to fetch using ref (doesn't cause fetch recreation)
      const page = append ? currentPageRef.current + 1 : 1;

      // Build query parameters using refs (always have latest values without causing recreation)
      const params: Record<string, string> = {
        page: page.toString(),
        pageSize: pageSizeRef.current.toString(), // Use ref for dynamic pageSize
        sortField: sortFieldRef.current, // Use ref
        sortDirection: sortDirectionRef.current, // Use ref
        ...Object.entries(additionalParamsRef.current).reduce(
          (acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              acc[key] = String(value);
            }
            return acc;
          },
          {} as Record<string, string>,
        ),
      };

      const queryParams = new URLSearchParams(params).toString();

      console.log(
        `\n🌐 [FRONTEND] Fetching data from: ${endpoint}?${queryParams}`,
      );
      console.log("[FRONTEND] Append mode:", append, "| Page:", page);

      try {
        const response = await fetchWithAuth(
          `${endpoint}?${queryParams}`,
          { method: "GET" },
          sessionRef.current,
        );

        console.log(
          "[FRONTEND] Response status:",
          response.status,
          response.statusText,
        );

        if (response.ok) {
          let result = await response.json();

          console.log("[FRONTEND] Raw response received:", {
            hasDataArray: Array.isArray(result),
            hasDataProp: "data" in result,
            hasPaginationProp: "pagination" in result,
            hasMetaProp: "meta" in result,
            resultKeys: Object.keys(result || {}),
          });

          let responseData: T[];
          let paginationInfo: any = null;

          // Check if response has pagination metadata (new format)
          if (
            result &&
            typeof result === "object" &&
            "data" in result &&
            "totalCount" in result &&
            "hasMore" in result
          ) {
            // Preferred format: { data: [...], totalCount: number, hasMore: boolean }
            paginationInfo = {
              total: result.totalCount,
              hasMore: result.hasMore,
            };
            responseData = result.data;
            console.log("[FRONTEND] Using NEW format (data/totalCount/hasMore)");
          } else if (
            result &&
            typeof result === "object" &&
            "data" in result &&
            "pagination" in result
          ) {
            // Alternative format: { data: [...], pagination: { hasMore, ... } }
            paginationInfo = result.pagination;
            responseData = result.data;
            console.log("[FRONTEND] Using NEW pagination format");
          } else if (
            result &&
            typeof result === "object" &&
            "data" in result &&
            "meta" in result
          ) {
            // Alternative format: { data: [...], meta: { ... } }
            paginationInfo = result.meta;
            responseData = result.data;
            console.log("[FRONTEND] Using META pagination format");
          } else {
            // Old format: just an array
            responseData = result;
            console.log("[FRONTEND] Using OLD array format");
          }

          console.log("[FRONTEND] Processed response:", {
            dataCount: responseData?.length || 0,
            paginationInfo,
            firstItem: responseData?.[0]
              ? Object.keys(responseData[0]).slice(0, 5)
              : "no items",
          });

          // Transform response if transformer provided (use ref for latest value)
          if (transformResponseRef.current) {
            responseData = transformResponseRef.current(responseData);
          }

          if (append) {
            // Append mode: add to existing data
            setData((prev) => {
              const newData = [...prev, ...responseData];
              console.log(
                `[FRONTEND] ✓ Appended ${responseData.length} items. Total: ${newData.length}`,
              );
              return newData;
            });
            setCurrentPage((prev) => prev + 1);
            currentPageRef.current = page; // Sync ref with new page
          } else {
            // Replace mode: set new data
            setData(responseData);
            console.log(
              `[FRONTEND] ✓ Set ${responseData.length} items as new data`,
            );
            // currentPageRef already set to 1 earlier
          }

          // Extract totalCount from pagination info
          if (paginationInfo && typeof paginationInfo.total === "number") {
            setTotalCount(paginationInfo.total);
            console.log(
              "[FRONTEND] TotalCount from pagination:",
              paginationInfo.total,
            );
          }

          // Determine hasMore based on available information
          if (paginationInfo && typeof paginationInfo.hasMore === "boolean") {
            // Use explicit hasMore from pagination metadata (most reliable)
            setHasMore(paginationInfo.hasMore);
            console.log(
              "[FRONTEND] HasMore from pagination:",
              paginationInfo.hasMore,
            );
          } else {
            // Fallback: if we got less than pageSize, we've reached the end
            const calculatedHasMore = responseData.length === pageSizeRef.current;
            setHasMore(calculatedHasMore);
            console.log(
              "[FRONTEND] HasMore calculated:",
              calculatedHasMore,
              `(got ${responseData.length}, expected ${pageSizeRef.current})`,
            );
          }

          // Success callback (use ref for latest value)
          if (onFetchSuccessRef.current) {
            onFetchSuccessRef.current(responseData, append);
          }
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (error) {
        console.error("Error fetching data:", error);

        // Error callback (use ref for latest value)
        if (onFetchErrorRef.current) {
          onFetchErrorRef.current(error);
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false; // Reset ref to allow next lazy load
      }
    },
    [
      endpoint,
      // Removed to prevent fetch recreation and keep it completely stable:
      // - pageSize, sortField, sortDirection (using refs for dynamic values)
      // - session (using sessionRef)
      // - currentPage (using currentPageRef)
      // - additionalParams (using additionalParamsRef)
      // - transformResponse (using transformResponseRef)
      // - onFetchSuccess (using onFetchSuccessRef)
      // - onFetchError (using onFetchErrorRef)
    ],
  );

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    setData([]);
    setCurrentPage(1);
    currentPageRef.current = 1; // Sync ref
    setHasMore(true);
    setIsLoading(false);
    setIsLoadingMore(false);
  }, []);

  /**
   * Set up IntersectionObserver for infinite scroll
   * Created ONCE on mount, never recreated - uses refs to always have latest values
   */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchTime.current;

        console.log('🔍 [IntersectionObserver] Triggered', {
          isIntersecting: entries[0].isIntersecting,
          hasMore: hasMoreRef.current,
          isLoading: isLoadingRef.current,
          isLoadingMore: isLoadingMoreRef.current,
          dataLength: dataLengthRef.current,
          timeSinceLastFetch,
          cooldown: FETCH_COOLDOWN
        });

        if (
          entries[0].isIntersecting &&
          hasMoreRef.current && // Always fresh value via ref
          !isLoadingRef.current && // Always fresh value via ref
          !isLoadingMoreRef.current && // Already using ref
          dataLengthRef.current > 0 && // Always fresh value via ref - prevent trigger during initial mount
          timeSinceLastFetch > FETCH_COOLDOWN // Debounce: prevent multiple fetches within 2 seconds
        ) {
          console.log('✅ [IntersectionObserver] All conditions met - fetching more data');
          isLoadingMoreRef.current = true; // Set ref BEFORE calling fetch to prevent race condition
          lastFetchTime.current = now;
          fetch(true); // Append mode
        }
      },
      {
        threshold: 0.1, // Trigger when 10% visible
        rootMargin: "800px", // Trigger 800px before element enters viewport (≈20 rows early)
      },
    );

    // Store observer reference for potential re-attachment
    observerInstanceRef.current = observer;

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependencies - observer created ONCE on mount, never recreated. All values accessed via refs.

  /**
   * Re-attach observer when target element changes (safety net for conditional rendering)
   * This handles cases where the trigger div gets removed and re-created
   */
  useEffect(() => {
    const observer = observerInstanceRef.current;
    const target = observerTarget.current;

    if (observer && target) {
      observer.observe(target);
      return () => observer.unobserve(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observerTarget.current]); // Re-run when target changes

  /**
   * Initial fetch on mount (only when session becomes ready)
   * Skip if initialData was provided (data already in state from cache)
   */
  useEffect(() => {
    if (session && initialMountRef.current) {
      initialMountRef.current = false;
      if (!hasInitialData) {
        fetch(false);
      }
    }
  }, [session]); // Only depend on session to trigger when it becomes ready

  /**
   * Re-fetch when additionalParams or pageSize change (after initial mount)
   * This ensures filtering/sorting triggers a fresh data fetch
   * Uses deep comparison to avoid infinite loops from object reference changes
   */
  useEffect(() => {
    // Skip on initial mount (handled by initial fetch effect above)
    if (initialMountRef.current) {
      return;
    }

    // Only re-fetch if session is ready
    if (!session) {
      return;
    }

    // Deep compare additionalParams using JSON stringify
    const currentParamsString = JSON.stringify(additionalParams);
    const paramsChanged = currentParamsString !== previousParamsRef.current;

    // CRITICAL FIX: Trigger on ANY pageSize change, not just 5x changes
    // The old logic caused stale data when pageSize changed from 30 to 50, etc.
    const pageSizeChanged = pageSize !== previousPageSizeRef.current;

    if (paramsChanged || pageSizeChanged) {
      if (paramsChanged) {
        console.log('🔄 [useLazyLoading] Filter params changed, re-fetching data', {
          old: previousParamsRef.current,
          new: currentParamsString
        });
        previousParamsRef.current = currentParamsString;
      }

      if (pageSizeChanged) {
        console.log('📏 [useLazyLoading] PageSize changed, re-fetching data', {
          old: previousPageSizeRef.current,
          new: pageSize
        });
        previousPageSizeRef.current = pageSize;
      }

      // Reset and fetch fresh data
      reset();
      fetch(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalParams, pageSize, session]); // Re-fetch when params or pageSize change

  /**
   * Re-fetch when endpoint changes (after initial mount)
   * This handles cases like filtering by building where the endpoint URL changes
   * Example: /renovation/documents/paginated -> /renovation/building/2000-NATI-144/documents/paginated
   */
  useEffect(() => {
    const previousEndpoint = previousEndpointRef.current;

    console.log('🔍 [useLazyLoading] Endpoint change detection effect running', {
      endpoint,
      previousEndpoint,
      isInitialMount: initialMountRef.current,
      hasSession: !!session,
      willTriggerRefetch: !initialMountRef.current && !!session && endpoint !== previousEndpoint
    });

    // Skip on initial mount (handled by initial fetch effect above)
    if (initialMountRef.current) {
      console.log('⏭️  [useLazyLoading] Skipping endpoint change - initial mount');
      return;
    }

    // Only re-fetch if session is ready
    if (!session) {
      console.log('⏭️  [useLazyLoading] Skipping endpoint change - no session');
      return;
    }

    // Detect endpoint change by comparing with stored ref
    if (endpoint !== previousEndpoint) {
      console.log('🔄 [useLazyLoading] ✅ ENDPOINT CHANGED - TRIGGERING RE-FETCH', {
        old: previousEndpoint,
        new: endpoint,
        timestamp: new Date().toISOString()
      });

      // Update the ref AFTER we've detected the change
      previousEndpointRef.current = endpoint;

      // Reset and fetch fresh data
      console.log('🔄 [useLazyLoading] Calling reset() and fetch(false)...');
      reset();
      fetch(false);
    } else {
      console.log('⏭️  [useLazyLoading] Skipping - endpoint unchanged');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, session]); // Re-fetch when endpoint changes

  return {
    data,
    currentPage,
    hasMore,
    totalCount,
    isLoading,
    isLoadingMore,
    fetch,
    observerTarget,
    setData,
    reset,
  };
}
